import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Reveal, FadeIn } from "../components/site/Reveal";
import { PrimaryButton } from "../components/site/Buttons";
import { INSIGHTS } from "../data/content";
import { fetchInsight, fetchInsights } from "../lib/api";
import { RichParagraphs } from "../components/site/RichText";
import { ArrowLeft } from "lucide-react";

export default function InsightDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(() => INSIGHTS.find((a) => a.slug === slug) || null);
  const [list, setList] = useState(INSIGHTS);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    Promise.all([fetchInsight(slug).catch(() => null), fetchInsights().catch(() => [])]).then(
      ([a, l]) => {
        if (!active) return;
        if (Array.isArray(l) && l.length) setList(l);
        if (a) setArticle(a);
        else if (!INSIGHTS.find((x) => x.slug === slug)) setNotFound(true);
      }
    );
    return () => {
      active = false;
    };
  }, [slug]);

  if (notFound) return <Navigate to="/insights" replace />;
  if (!article)
    return <div className="pt-52 pb-40 yadid-container text-ink/40 font-light">Loading…</div>;

  const idx = list.findIndex((a) => a.slug === slug);
  const next = list[(idx + 1) % list.length] || list[0];

  return (
    <div data-testid="insight-detail-page">
      <article className="pt-40 md:pt-52 pb-8">
        <div className="yadid-container max-w-4xl">
          <FadeIn>
            <Link
              to="/insights"
              className="link-underline eyebrow text-ink/60 inline-flex items-center gap-2 mb-12"
            >
              <ArrowLeft size={14} /> All Insights
            </Link>
          </FadeIn>
          <Reveal>
            <p className="eyebrow text-brass mb-6">
              {article.category} <span className="text-ink/30 mx-2">/</span> {article.date}
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.02em] text-ink">
              {article.title}
            </h1>
          </Reveal>
        </div>
      </article>

      <section className="bg-ivory">
        <div className="yadid-container max-w-3xl py-12 md:py-16">
          <Reveal>
            <RichParagraphs paragraphs={article.body} lead />
          </Reveal>
        </div>
      </section>

      {/* Next + CTA */}
      <section className="bg-stone-soft/40 border-t border-stone">
        <div className="yadid-container py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <p className="eyebrow text-brass mb-6">Continue Reading</p>
              <Link to={`/insights/${next.slug}`} className="group inline-block">
                <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-ink tracking-[-0.02em] leading-tight link-underline">
                  {next.title}
                </h2>
              </Link>
            </div>
            <div className="lg:col-span-4 lg:col-start-9">
              <PrimaryButton to="/contact">Start a Conversation</PrimaryButton>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
